import React, {useCallback} from 'react';
import {Grid, makeStyles, Tooltip} from '@material-ui/core';
import {observer} from 'mobx-react-lite';
import {DatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import {NumberFormatCustom, PhoneFormatCustom, SsnFormatCustom, SwitchCustom, TextBox} from '../../../Core/UI';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {CategoryCard} from '../CategoryCard';
import {Approval} from '../../../Core/mobx/ParameterStore/Approval';
import {SelectInputElement} from '../../PreQualification/Components/SelectInputElement';
import EmploymentQuestions from '../../Questions/Employment';
import Basic from '../../Questions/Basic';
import HeightWeight from '../../Questions/HeightWeight';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import DateFnsUtils from '@date-io/date-fns';
import {ssnValidator, phoneValidator, emailValidator} from '../../../Core/helpers/validators';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/ApprovalXref';

import RootStore from '../../../Core/mobx/RootStore';
import AddressVerificationSection from '../../Common/AddressVerificationSection';
import {AddressKey} from '../../../Core/mobx/AddressVerificationStore/AddressVerificationStore';

const useStyles = makeStyles({
    tooltip: {
        maxWidth: 500,
        width: '100%',
        background: '#fff',
        border: '1px solid gray',
        borderRadius: '10px',
        padding: '0 10px',
        margin: 0,
        color: '#444',
        fontFamily: 'FredokaRegular',
        fontSize: '18px',
        lineHeight: '24px',
        '& h1': {
            color: '#456d69',
            fontWeight: 600,
            fontSize: '26px',
            lineHeight: '26.px',
        },
    },
});

type BasicInfoProps = {
    valueChanged: (p?: Parameter & {name?: string}, value?: any, name?: string) => void;
    parameters: Approval;
    setCategory: (entry: string) => void;
    index: number;
    onAcceptStandardized: (key: AddressKey) => void;
};

const BasicInfo = observer(({valueChanged, parameters, setCategory, index, onAcceptStandardized}: BasicInfoProps) => {
    const classes = useStyles();
    const required = (value?: string) => (
        <span>
            {value && value} <span style={{color: 'red'}}>*</span>
        </span>
    );

    const phoneValue = useCallback(() => {
        const rawValue = parameters.phoneNumber.value;
        if (!rawValue) return rawValue;

        const numericValue = rawValue.replace(/[^0-9]/g, '');
        return numericValue.slice(-10);
    }, [parameters.phoneNumber.value]);

    return (
        <CategoryCard title={'Basic Information'} setCategory={setCategory} index={index}>
            <Grid item xs={4}>
                <TextBox
                    id={'firstName'}
                    label={required('Client First Name')}
                    value={parameters.firstName?.value}
                    onChange={(value) => valueChanged(parameters.firstName, value)}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id={'lastName'}
                    label={required('Client Last Name')}
                    value={parameters.lastName?.value}
                    onChange={(value) => valueChanged(parameters.lastName, value)}
                />
            </Grid>
            <Grid item xs={2}>
                <TextBox
                    id={'middleName'}
                    label={'Middle Initial'}
                    value={parameters.middleName?.value}
                    onChange={(value) => valueChanged(parameters.middleName, value)}
                />
            </Grid>
            <Grid item xs={2}>
                <TextBox
                    id={'suffix'}
                    label={'Suffix'}
                    value={parameters.suffix?.value}
                    onChange={(value) => valueChanged(parameters.suffix, value)}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    error={!!parameters.phoneNumber.value && !phoneValidator(phoneValue())}
                    id={'phoneNumber'}
                    label={required('Phone Number')}
                    value={phoneValue()}
                    onChange={(value) => valueChanged(parameters.phoneNumber, value)}
                    placeholder="(323) 232-3123"
                    InputProps={{
                        inputComponent: PhoneFormatCustom,
                    }}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id={'bestTimeToCall'}
                    label={'Best Time To Call'}
                    value={parameters.bestTimeToCall?.value}
                    onChange={(value) => valueChanged(parameters.bestTimeToCall, value)}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    error={!!parameters.emailAddress.value && !emailValidator(parameters.emailAddress.value)}
                    id={'emailAddress'}
                    label={required('Email Address')}
                    value={parameters.emailAddress?.value}
                    onChange={(value) => valueChanged(parameters.emailAddress, value?.trim())}
                    placeholder="example@gmail.com"
                />
            </Grid>
            <Basic valueChanged={valueChanged} preapproval parameters={parameters} />
            <Grid item xs={12}>
                <AddressVerificationSection
                    addressKey="client"
                    label="Client Address"
                    fields={{
                        street: parameters.address?.value || '',
                        city: parameters.addressCity?.value || '',
                        state: parameters.state?.value || '',
                        zip: parameters.addressZipCode?.value || '',
                        unit: parameters.addressUnit?.value || '',
                    }}
                    onFieldChange={(field, value) => {
                        const map = {
                            street: parameters.address,
                            city: parameters.addressCity,
                            state: parameters.state,
                            zip: parameters.addressZipCode,
                            unit: parameters.addressUnit,
                        };
                        valueChanged(map[field], value);
                    }}
                    onAcceptStandardized={() => onAcceptStandardized('client')}
                />
            </Grid>
            <HeightWeight parameters={parameters} valueChanged={valueChanged} preapproval />
            <Grid item xs={4}>
                <SwitchCustom
                    param={{
                        name: 'weightChanged',
                        label: '10lbs changed in the 12 months?',
                        ...parameters.weightChanged,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id={'incomeIndividual'}
                    label={required('Individual Annual Income')}
                    value={parameters.incomeIndividual?.value}
                    onChange={(value) => valueChanged(parameters.incomeIndividual, value)}
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                    }}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id={'incomeHouseholdText'}
                    label={required('Household Income Actual')}
                    value={parameters.incomeHouseholdText?.value}
                    onChange={(value) =>
                        valueChanged({...parameters.incomeHouseholdText, name: 'incomeHouseholdText'}, value)
                    }
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                    }}
                />
            </Grid>
            <EmploymentQuestions valueChanged={valueChanged} parameters={parameters} preapproval />
            <Grid item xs={4}>
                <TextBox
                    id={'estimatedSavingsTotal'}
                    label={'Estimated Savings Total'}
                    value={parameters.estimatedSavingsTotal?.value}
                    onChange={(value) => valueChanged(parameters.estimatedSavingsTotal, value)}
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                    }}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id={'estimatedDebtTotal'}
                    label={'Estimated Debt Total'}
                    value={parameters.estimatedDebtTotal?.value}
                    onChange={(value) => valueChanged(parameters.estimatedDebtTotal, value)}
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                    }}
                />
            </Grid>
            <Grid item xs={4}>
                <SwitchCustom
                    param={{
                        name: 'travelingOut',
                        label: 'Travel outside U.S 12 months',
                        ...parameters.travelingOut,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            <>
                {parameters.travelingOut.value === true && (
                    <>
                        <Grid item xs={4}>
                            <TextBox
                                id={'travelingPlace'}
                                label={required('Place that you are travelling to ?')}
                                value={parameters.travelingPlace?.value}
                                onChange={(value) => valueChanged(parameters.travelingPlace, value)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                id={'travelDuration'}
                                label={required('How long do you plan to stay outside U.S ?')}
                                value={parameters.travelDuration?.value}
                                onChange={(value) => valueChanged(parameters.travelDuration, value)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                id={'travelReason'}
                                label={required('Reason for travel?')}
                                value={parameters.travelReason?.value}
                                onChange={(value) => valueChanged(parameters.travelReason, value)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                <DatePicker
                                    inputVariant="outlined"
                                    variant="inline"
                                    openTo="year"
                                    views={['year', 'month']}
                                    label={'Date of planned travel?'}
                                    style={{width: '100%', marginTop: 8}}
                                    value={parameters.travelDates?.value || null}
                                    onChange={(date) => valueChanged(parameters.travelDates, date)}
                                />
                            </MuiPickersUtilsProvider>
                        </Grid>
                    </>
                )}
            </>
            <Grid item xs={4}>
                <SwitchCustom
                    param={{
                        name: 'sameOwner',
                        label: 'Is Insured Same as Owner',
                        ...parameters.sameOwner,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            <>
                {parameters.sameOwner.value === false && (
                    <>
                        <Grid item xs={12}>
                            <div style={{fontSize: 20, color: '#5D6CC1', fontWeight: 'bold', marginTop: '1em'}}>
                                Policy Owner Information
                            </div>
                        </Grid>
                        <Grid item xs={4}>
                            <div style={{float: 'left', width: '100%', maxWidth: 'calc(100% - 24px)'}}>
                                <TextBox
                                    id={'policyOwnerName'}
                                    label={required("Policy Owner's Name")}
                                    value={parameters.policyOwnerName?.value}
                                    onChange={(value) => valueChanged(parameters.policyOwnerName, value)}
                                />
                            </div>
                            <Tooltip
                                arrow
                                leaveDelay={200}
                                placement="right"
                                classes={{tooltip: classes.tooltip}}
                                title={
                                    <>
                                        <h1>Don’t Forget!</h1>
                                        <p>Please include middle initial and suffix if possible.</p>
                                    </>
                                }
                            >
                                <InfoOutlinedIcon color="primary" />
                            </Tooltip>
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                error={
                                    !!parameters.ownerSocialSecurityNumber.value &&
                                    !ssnValidator(parameters.ownerSocialSecurityNumber.value)
                                }
                                id={'ownerSocialSecurityNumber'}
                                label={required('SSN')}
                                value={parameters.ownerSocialSecurityNumber?.value}
                                onChange={(value) => valueChanged(parameters.ownerSocialSecurityNumber, value)}
                                placeholder="394739873"
                                InputProps={{
                                    inputComponent: SsnFormatCustom,
                                }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                id={'ownerBMonth'}
                                label={required('Birth Month')}
                                value={parameters.ownerBMonth?.value}
                                onChange={(value) => valueChanged(parameters.ownerBMonth, value)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                id={'ownerBday'}
                                label={required('Birth Day')}
                                type="number"
                                value={parameters.ownerBday?.value}
                                onChange={(value) => valueChanged(parameters.ownerBday, value)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                id={'ownerByear'}
                                label={required('Birth Year')}
                                type="number"
                                value={parameters.ownerByear?.value}
                                onChange={(value) => valueChanged(parameters.ownerByear, value)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <SelectInputElement
                                param={{
                                    label: "Driver's License or State ID",
                                    name: 'ownerIssueState',
                                    options: fieldOptions.ownerIssueState.options,
                                    ...parameters.ownerIssueState,
                                }}
                                required={
                                    RootStore.agentPortalStore.parameterStore.approval.agentProducts.value.includes(
                                        'FGPathsetterIUL',
                                    )
                                        ? required('')
                                        : ''
                                }
                                valueChanged={valueChanged}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                id={'ownerNumber'}
                                label={
                                    RootStore.agentPortalStore.parameterStore.approval.agentProducts.value.includes(
                                        'FGPathsetterIUL',
                                    )
                                        ? required('License or ID Number')
                                        : 'License or ID Number'
                                }
                                value={parameters.ownerNumber?.value}
                                onChange={(value) => valueChanged(parameters.ownerNumber, value)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                id={'ownerExpiration'}
                                label={'Expiration Date'}
                                value={parameters.ownerExpiration?.value}
                                onChange={(value) => valueChanged(parameters.ownerExpiration, value)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                id={'amountOfCoverageMomHas'}
                                label={'Mother Total Amount Of Inforce'}
                                value={parameters.amountOfCoverageMomHas?.value}
                                onChange={(value) => valueChanged(parameters.amountOfCoverageMomHas, value)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                id={'amountOfCoverageDadHas'}
                                label={'Father Total Amount Of Inforce'}
                                value={parameters.amountOfCoverageDadHas?.value}
                                onChange={(value) => valueChanged(parameters.amountOfCoverageDadHas, value)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                id={'ownerCitizenshipCountry'}
                                label={
                                    ['FGPathsetterIUL', 'MOOIULE'].some((value) =>
                                        RootStore.agentPortalStore.parameterStore.approval.agentProducts.value.includes(
                                            value,
                                        ),
                                    )
                                        ? required('Owner Citizenship Country')
                                        : 'Owner Citizenship Country'
                                }
                                value={parameters.ownerCitizenshipCountry?.value}
                                onChange={(value) => valueChanged(parameters.ownerCitizenshipCountry, value)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                id={'relationshipToInsured'}
                                label={required('Relationship To Insured')}
                                value={parameters.relationshipToInsured?.value}
                                onChange={(value) => valueChanged(parameters.relationshipToInsured, value)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                id={'policyOwnerPhone'}
                                label={required('Policy Owner Phone')}
                                value={parameters.policyOwnerPhone?.value}
                                onChange={(value) => valueChanged(parameters.policyOwnerPhone, value)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                id={'policyOwnerEmail'}
                                label={
                                    ['LSWIUL', 'FGPathsetterIUL'].some((value) =>
                                        RootStore.agentPortalStore.parameterStore.approval.agentProducts.value.includes(
                                            value,
                                        ),
                                    ) ||
                                    RootStore.agentPortalStore.parameterStore.approval.agentProducts.value.length == 0
                                        ? required('Policy Owner Email')
                                        : 'Policy Owner Email'
                                }
                                value={parameters.policyOwnerEmail?.value}
                                onChange={(value) => valueChanged(parameters.policyOwnerEmail, value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <AddressVerificationSection
                                addressKey="policyOwner"
                                label="Policy Owner Address"
                                stateOptions={fieldOptions.policyOwnerState.options}
                                fields={{
                                    street: parameters.policyOwnerStreetAddress?.value || '',
                                    city: parameters.policyOwnerCity?.value || '',
                                    state: parameters.policyOwnerState?.value || '',
                                    zip: parameters.policyOwnerZipcode?.value || '',
                                    unit: parameters.policyOwnerUnit?.value || '',
                                }}
                                onFieldChange={(field, value) => {
                                    const map = {
                                        street: parameters.policyOwnerStreetAddress,
                                        city: parameters.policyOwnerCity,
                                        state: parameters.policyOwnerState,
                                        zip: parameters.policyOwnerZipcode,
                                        unit: parameters.policyOwnerUnit,
                                    };
                                    valueChanged(map[field], value);
                                }}
                                onAcceptStandardized={() => onAcceptStandardized('policyOwner')}
                            />
                        </Grid>
                    </>
                )}
            </>
        </CategoryCard>
    );
});

export default BasicInfo;
